import {
  getProjectOrganisationIds,
  getUserProjectIds,
  getUserProjectIdsWithDirectAccess,
} from '@resistapp/common/access';
import { AllProjectEnvironmentTypesGroup } from '@resistapp/common/comparable-env-groups';
import { getFriendlyInterval } from '@resistapp/common/date-utils';
import { isAdmin } from '@resistapp/common/features';
import { DEFAULT_END_INTERVAL, DEFAULT_START_INTERVAL } from '@resistapp/common/friendly';
import { FullProject, Organisation, SampleStatus, SmartChipFlag, User } from '@resistapp/common/types';
import { values } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import useLocalStorageState from 'use-local-storage-state';
import { LocalStorageId } from '../components/shared/local-storage';
import { useSampleDataContext } from '../contexts/sample-data-context';
import { useUser } from '../contexts/use-user-context';
import { useOrganisations, usePatchProjectSamples, usePatchSampleStatuses, useUsers } from './api';
import { useFilters } from './use-query-filters/use-query-filters';

export interface ResearchAdminFeatures {
  updateSampleStatuses: (newStatus: SampleStatus, environmentId?: number) => void;
  isModifyAsAdminActive: boolean;
  setIsModifyAsAdminActive: (value: boolean) => void;
  project: FullProject | null;
  patchSamplesToProject: ReturnType<typeof usePatchProjectSamples>;
  usersWithAccess: User[];
  usersWithDirectAccess: User[];
  usersWithoutAccess: User[];
  organisationsWithAccess: Organisation[];
  organisationsWithoutAccess: Organisation[];
  highlightFlag: SmartChipFlag | null;
  setHighlightFlag: (flag: SmartChipFlag | null) => void;
  getAccessDifferences: (targetProjectId: number) => {
    usersOnlyInTarget: User[];
    usersOnlyInCurrent: User[];
    orgsOnlyInTarget: Organisation[];
    orgsOnlyInCurrent: Organisation[];
  };
}

export function useResearchAdmin(): ResearchAdminFeatures {
  const { data: project, refetchData } = useSampleDataContext();
  const { data: allUsers, send: loadUsersStable } = useUsers();
  const { data: allOrganisations } = useOrganisations();
  const filters = useFilters();
  const { isAdmin: loggedInUserIsAdmin } = useUser();
  const patchSamplesToProject = usePatchProjectSamples();
  const [_isModifyAsAdminActive, setIsModifyAsAdminActive] = useLocalStorageState<boolean>(LocalStorageId.adminMode, {
    defaultValue: false,
  });
  const isModifyAsAdminActive = _isModifyAsAdminActive && loggedInUserIsAdmin;
  type StoredFlagValue = 'None' | SmartChipFlag;

  // Persist highlight flag selection in local storage (shared with Chip View)
  const [storedHighlightFlag, setStoredHighlightFlag] = useLocalStorageState<StoredFlagValue>(
    LocalStorageId.chipViewHighlightFlag,
    {
      defaultValue: 'None',
    },
  );

  const highlightFlag: SmartChipFlag | null =
    storedHighlightFlag === 'None' ? null : (storedHighlightFlag as SmartChipFlag);

  const setHighlightFlag = useCallback(
    (flag: SmartChipFlag | null) => {
      setStoredHighlightFlag(flag ?? 'None');
    },
    [setStoredHighlightFlag],
  );

  const { mutate: patchSampleStatuses } = usePatchSampleStatuses(
    () => {
      refetchData();
    },
    (err: Error) => {
      console.error('Failed to update sample statuses:', err);
    },
  );

  const { selectedEnvironmentTypeGroup, interval } = filters;
  const updateSampleStatuses = useCallback(
    (newStatus: SampleStatus, environmentId?: number) => {
      if (!project?.focusedByUID) return;
      const focusedSamples = values(project.focusedByUID).flat();
      if (!environmentId) {
        const envType =
          selectedEnvironmentTypeGroup === AllProjectEnvironmentTypesGroup.ALL_PROJECT_ENVIRONMENTS
            ? ''
            : `${selectedEnvironmentTypeGroup} samples`;

        const intervalText =
          interval.start.getTime() === DEFAULT_START_INTERVAL.getTime() &&
          interval.end.getTime() === DEFAULT_END_INTERVAL.getTime()
            ? ''
            : ` (${getFriendlyInterval([{ date: interval.start }, { date: interval.end }])})`;
        const allSamples = values(project.samplesByUID).flat();
        const confirmationText =
          focusedSamples.length === allSamples.length
            ? `Setting status ${newStatus} for all ${allSamples.length} project samples.\n\nAre you sure?`
            : `Setting status ${newStatus} for ${focusedSamples.length} ${envType}${intervalText}\nout of all ${allSamples.length} project samples.\n\nAre you sure?`;
        const confirmed = window.confirm(confirmationText);
        if (!confirmed) return;
      }
      const newStatuses = focusedSamples
        .filter(data => (environmentId ? data.environment.id === environmentId : true))
        .map(sample => {
          return { id: sample.id, status: newStatus };
        });
      patchSampleStatuses(newStatuses);
    },
    [project, patchSampleStatuses, selectedEnvironmentTypeGroup, interval],
  );

  const usersWithAccess = useMemo(
    () => allUsers?.filter(u => !isAdmin(u) && getUserProjectIds(u).includes(project?.id ?? -1)) || [],
    [allUsers, project?.id],
  );

  const usersWithDirectAccess = useMemo(
    () => allUsers?.filter(u => !isAdmin(u) && getUserProjectIdsWithDirectAccess(u).includes(project?.id ?? -1)) || [],
    [allUsers, project?.id],
  );

  const usersWithoutAccess = useMemo(
    () => allUsers?.filter(u => !isAdmin(u) && !getUserProjectIds(u).includes(project?.id ?? -1)) || [],
    [allUsers, project?.id],
  );

  const organisationsWithAccess = useMemo(
    () => allOrganisations?.filter(org => getProjectOrganisationIds(org).includes(project?.id ?? -1)) || [],
    [allOrganisations, project?.id],
  );

  const organisationsWithoutAccess = useMemo(
    () => allOrganisations?.filter(org => !getProjectOrganisationIds(org).includes(project?.id ?? -1)) || [],
    [allOrganisations, project?.id],
  );

  const getAccessDifferences = useCallback(
    (targetProjectId: number) => {
      // Get access rights for the target project
      const targetProjectUsers = usersWithAccess.filter(u => getUserProjectIds(u).includes(targetProjectId));
      const targetProjectOrgs = organisationsWithAccess.filter(org =>
        getProjectOrganisationIds(org).includes(targetProjectId),
      );

      // Compare access rights between current and target project
      const usersOnlyInTarget = targetProjectUsers.filter(
        targetUser => !usersWithAccess.some(currentUser => currentUser.id === targetUser.id),
      );
      const usersOnlyInCurrent = usersWithAccess.filter(
        currentUser => !targetProjectUsers.some(targetUser => targetUser.id === currentUser.id),
      );

      // Compare organisation access rights
      const orgsOnlyInTarget = targetProjectOrgs.filter(
        targetOrg => !organisationsWithAccess.some(currentOrg => currentOrg.id === targetOrg.id),
      );
      const orgsOnlyInCurrent = organisationsWithAccess.filter(
        currentOrg => !targetProjectOrgs.some(targetOrg => targetOrg.id === currentOrg.id),
      );

      return {
        usersOnlyInTarget,
        usersOnlyInCurrent,
        orgsOnlyInTarget,
        orgsOnlyInCurrent,
      };
    },
    [usersWithAccess, organisationsWithAccess],
  );

  useEffect(() => {
    if (loggedInUserIsAdmin) {
      void loadUsersStable();
    }
  }, [loggedInUserIsAdmin]);

  return {
    updateSampleStatuses,
    isModifyAsAdminActive,
    setIsModifyAsAdminActive,
    project: project || null,
    patchSamplesToProject,
    usersWithAccess,
    usersWithDirectAccess,
    usersWithoutAccess,
    organisationsWithAccess,
    organisationsWithoutAccess,
    highlightFlag,
    setHighlightFlag,
    getAccessDifferences,
  };
}
